<template>
  <b-button
    class="loading-button"
    :variant="variant"
    :disabled="shouldDisable"
    :block="block"
    :size="size"
    @click="onClick"
  >
    <loading
      v-if="anyLoading"
      class="loading-button__loading"
    />
    <span
      v-else-if="$slots.icon"
      class="loading-button__icon mr-2"
    >
      <slot name="icon" />
    </span>
    <slot/>
  </b-button>
</template>

<script>
import loading from '@/components/Loading';
/**
 * Botão com função de loading.
 *
 * Este botão serve para disparar ações mais demoradas, pois ao click ele exibe o loading padrão
 * do sistema ao lado do conteúdo setado no slot padrão. O botão funciona da mesma maneira que o
 * b-button do vue-bootstrap, exceto o evento enviado por parametro no evento de click, quando um
 * evento click é disparado, ele possui as seguintes propriedades:
 *
 * @property {Function} start Função para informar que a ação executada no evento de click
 * começou, o que disparará a animação de carregamento.
 * @property {Function} complete Função para informar que a ação executada no evento de click
 * terminou.
 * @property {Function} wait Função function(Promise) para informar uma promessa da ação executada
 * no click para ser aguardada.
 * @property {Object} originalEvent Objeto do evento original disparado pelo b-button.
 *
 * @example
 *   <s-loading-button @click="$event.wait(doSomethingThatReturnsPromise())">
 *     (button content)
 *   </s-loading-button>
 */
export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'primary',
    },
    size: {
      type: String,
      required: false,
    },
    autoStart: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: [Boolean, Number],
      default: false,
    },
  },
  components: {
    loading,
  },
  data() {
    return {
      internalLoading: false,
    };
  },
  computed: {
    anyLoading() {
      return this.loading || this.internalLoading;
    },
    shouldDisable() {
      return this.disabled || this.anyLoading;
    },
  },
  methods: {
    onClick(event) {
      if (this.autoStart) {
        this.startLoading();
      }
      this.$emit('click', this.getEvent(event));
    },
    getEvent(event) {
      return {
        start: () => {
          this.startLoading();
        },
        complete: () => {
          this.endLoading();
        },
        wait: (promise) => {
          this.startLoading();
          return promise
            .then(() => {
              this.endLoading();
            })
            .catch((error) => {
              this.endLoading();
              return Promise.reject(error);
            });
        },
        originalEvent: event,
      };
    },
    startLoading() {
      this.internalLoading = true;
    },
    endLoading() {
      this.internalLoading = false;
    },
  },
};
</script>

<style lang="sass" scoped>
.loading-button
  display: inline-flex
  justify-content: center
  align-items: center
  &__loading
    margin-right: 5px
</style>
